import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Subject, Observable, Subscription, BehaviorSubject } from 'rxjs';
import { tap, map } from 'rxjs/operators';

import { environment } from '@environments/environment';
import { Vehicle } from '@classes/vehicles/vehicle';
import { VehicleHistory } from '@classes/vehicles/vehicle-history';
import { VehicleReport } from '@classes/vehicles/vehicle-report';

import * as FileSaver from 'file-saver';
import * as moment from 'moment';
import { PaginationRequest } from '@classes/pagination/pagination-request';
import { Pagination } from '@classes/pagination/pagination';
import { VehicleStatus } from '@classes/vehicles/vehicle-status';

@Injectable({
  providedIn: 'root',
})
export class VehiclesService {
  vehiclesListChanged = new Subject<Vehicle[]>();
  vehicleHistoriesListChanged = new Subject<VehicleHistory[]>();
  vehiclereportsListChanged = new Subject<VehicleReport[]>();
  paginationHistorySubject = new BehaviorSubject<Pagination>(null);

  private baseUrl: string = environment.apiUrl + 'vehicles';
  private vehicles: Vehicle[];
  private vehicleHistories: VehicleHistory[];
  private vehicleReports: VehicleReport[];
  private getListSub: Subscription;

  private lastQuery: string;

  constructor(private http: HttpClient) { }

  getList(includeDeleted: boolean = false): Observable<Vehicle[]> {
    let httpParams: HttpParams = new HttpParams();
    httpParams = httpParams.append('includeDeleted', includeDeleted);

    return this.http.get<Vehicle[]>(this.baseUrl, { params: httpParams }).pipe(
      map((vehicles) => {
        this.vehicles = vehicles.map((c) => Vehicle.fromObject(c));
        this.vehicles.sort((a, b) =>
          a.numberplate.localeCompare(b.numberplate),
        );
        this.vehiclesListChanged.next([...this.vehicles]);
        return [...this.vehicles];
      }),
    );
  }

  getStatusList(): Observable<VehicleStatus[]> {
    return this.http.get<VehicleStatus[]>(`${this.baseUrl}/status`).pipe(
      map((vehicles) => {
        vehicles = vehicles.sort((a, b) =>
          a.username.localeCompare(b.username),
        );
        return vehicles;
      }),
    );
  }

  getHistoryList(
    request: PaginationRequest,
    filters?: any,
    vehicleId?: string,
  ): void {
    if (this.getListSub) {
      this.getListSub.unsubscribe();
    }

    let httpParams = new HttpParams();
    const filtersQuery = [];

    if (request.pageSize && request.page) {
      httpParams = httpParams
        .append('pageSize', request.pageSize.toString())
        .append(
          'page',
          request.query !== this.lastQuery ? '1' : request.page.toString(),
        );
    }

    if (request.sort) {
      httpParams = httpParams.append('sorts', request.sort);
    }

    if (vehicleId) {
      filtersQuery.push(`vehicleId==${vehicleId}`);
    }

    if (filters) {
      if (filters.range?.fromStartDate) {
        filtersQuery.push(
          `from>=${moment(new Date(filters.range.fromStartDate)).startOf('day').utc().format('YYYY-MM-DD HH:mm')}`,
        );
      }
      if (filters.range?.toStartDate) {
        filtersQuery.push(
          `to<=${moment(new Date(filters.range.toStartDate)).endOf('day').utc().format('YYYY-MM-DD HH:mm')}`,
        );
      }
    }

    if (request.query) {
      const filters = request.query;
      filtersQuery.push(`(numberplate|username|comment)@=${filters}`);
    }

    if (filtersQuery.length > 0) {
      httpParams = httpParams.append('Filters', filtersQuery.join());
    }

    this.getListSub = this.http
      .get<
        VehicleHistory[]
      >(`${this.baseUrl}/history`, { params: httpParams, observe: 'response' })
      .subscribe((result) => {
        const pagination: Pagination = JSON.parse(
          result.headers.get('X-Pagination'),
        );
        this.vehicleHistories = result.body;
        this.vehicleHistoriesListChanged.next([...this.vehicleHistories]);
        this.paginationHistorySubject.next({ ...pagination });
      });

    this.lastQuery = request.query;
  }

  put(vehicle: Vehicle): Observable<Vehicle> {
    return this.http.put<Vehicle>(this.baseUrl, vehicle).pipe(
      tap((editedVehicle) => {
        editedVehicle = Vehicle.fromObject(editedVehicle);
        this.vehicles = this.vehicles.map((c) =>
          c.id === editedVehicle.id ? editedVehicle : c,
        );
        this.vehiclesListChanged.next([...this.vehicles]);
      }),
    );
  }

  patchHistory(vehicleId: string, mileage) {
    return this.http
      .patch<VehicleHistory>(`${this.baseUrl}/history/${vehicleId}`, mileage)
      .pipe(
        tap((editedVehicleHistory) => {
          this.vehicleHistories = this.vehicleHistories.map((c) =>
            c.id === editedVehicleHistory.id ? editedVehicleHistory : c,
          );
          this.vehicleHistoriesListChanged.next([...this.vehicleHistories]);
        }),
      );
  }

  post(vehicle: Vehicle): Observable<Vehicle> {
    return this.http.post<Vehicle>(this.baseUrl, vehicle).pipe(
      tap((createdVehicle) => {
        createdVehicle = Vehicle.fromObject(createdVehicle);
        this.vehicles.push(createdVehicle);
        this.vehiclesListChanged.next([...this.vehicles]);
      }),
    );
  }

  synchWithFMTrack(): Observable<Vehicle[]> {
    return this.http
      .post<Vehicle[]>(`${this.baseUrl}/sync`, null)
      .pipe(tap((vehicles) => console.log('vehicles', vehicles)));
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`).pipe(
      tap(() => {
        this.vehicles.find((vehicle: Vehicle) => vehicle.id === id).isDeleted = true;
        this.vehiclesListChanged.next([...this.vehicles]);
      }),
    );
  }

  downloadPhoto(id: string): Observable<Blob> {
    const url = `${this.baseUrl}/${id}/photo `;

    return this.http
      .get(url, { observe: 'response', responseType: 'blob' })
      .pipe(map((res) => this.saveFile(res)));
  }

  getPhoto(id: string): Observable<Blob> {
    return this.http
      .get(`${this.baseUrl}/${id}/photo`, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(map((res) => res.body));
  }

  private saveFile(res: HttpResponse<Blob>): Blob {
    FileSaver.saveAs(res.body, res.headers.get('filename'));
    return res.body;
  }
}
